import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  CalendarClock,
  ExternalLink,
  Image as ImageIcon,
  LayoutGrid,
  List,
  Loader2,
  Plus,
  Repeat,
  Send,
  Sparkles,
  Trash2,
  TriangleAlert,
  Upload
} from "lucide-react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { identidadeDaRede } from "./redes";
import Cabecalho from "./Cabecalho";
import useContasZernio from "./useContasZernio";

// Publicar e agendar nas redes conectadas, sem sair do helpdesk.
//
// O agendamento e a publicação são da Zernio: aqui não há fila própria nem
// job — duplicar isso significaria dois relógios discordando sobre quando o
// post sai.

// A Zernio grava "pending" no que está agendado à espera da hora; "scheduled"
// aparece no filtro da API. Os dois significam a mesma coisa para quem lê a
// tela, e faltar um deles deixava o selo em branco.
const SITUACOES = {
  published: {
    rotulo: "Publicado",
    classe: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
  },
  scheduled: {
    rotulo: "Agendado",
    classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400"
  },
  pending: {
    rotulo: "Agendado",
    classe: "bg-sky-500/10 text-sky-600 dark:text-sky-400"
  },
  draft: { rotulo: "Rascunho", classe: "bg-muted text-muted-foreground" },
  failed: { rotulo: "Falhou", classe: "bg-destructive/10 text-destructive" },
  partial: {
    rotulo: "Parcial",
    classe: "bg-amber-500/10 text-amber-600 dark:text-amber-400"
  }
};

const situacaoDe = (post) =>
  SITUACOES[post.status] || {
    rotulo: post.status || "—",
    classe: "bg-muted text-muted-foreground"
  };

const FILTROS = [
  { valor: "todos", rotulo: "Todas" },
  { valor: "scheduled", rotulo: "Agendadas" },
  { valor: "published", rotulo: "Publicadas" },
  { valor: "draft", rotulo: "Rascunhos" },
  { valor: "failed", rotulo: "Com falha" }
];

const formatarData = (valor) => {
  if (!valor) return "";

  try {
    return new Date(valor).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit"
    });
  } catch {
    return "";
  }
};

const idDoPost = (post) => post._id || post.id;

// As redes em que o post saiu, com a cor de cada uma. Aparece nas duas
// visões, então mora num lugar só.
const SelosDeRede = ({ post }) => (
  <div className="flex flex-wrap items-center gap-1">
    {(post.platforms || []).map((alvo) => {
      const rede = identidadeDaRede(alvo.platform);
      const Glifo = rede.icone;

      return (
        <span
          key={`${alvo.platform}-${alvo.accountId}`}
          title={rede.rotulo}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium"
          style={{ backgroundColor: `${rede.corSelo}1a`, color: rede.corSelo }}
        >
          <Glifo className="h-3 w-3" />
          {rede.rotulo}
        </span>
      );
    })}
  </div>
);

const Situacao = ({ post }) => {
  const situacao = situacaoDe(post);

  return (
    <span
      className={cn(
        "inline-block shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide",
        situacao.classe
      )}
    >
      {situacao.rotulo}
    </span>
  );
};

// O quadro: uma publicação por cartão, com a imagem quando existe. Serve para
// olhar o que foi produzido.
const Quadro = ({ posts, onAbrir }) => (
  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
    {posts.map((post) => {
      const capa = (post.mediaItems || [])[0]?.url;

      return (
        <button
          key={idDoPost(post)}
          type="button"
          onClick={() => onAbrir(post)}
          className="overflow-hidden rounded-xl border bg-card text-left transition-colors hover:border-foreground/20"
        >
          {capa ? (
            <img
              src={capa}
              alt=""
              className="h-36 w-full bg-muted object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex h-36 w-full items-center justify-center bg-muted/50 text-muted-foreground">
              <ImageIcon className="h-6 w-6" />
            </div>
          )}

          <div className="space-y-2 p-3">
            <div className="flex items-center justify-between gap-2">
              <Situacao post={post} />
              {(post.scheduledFor || post.publishedAt) && (
                <span className="text-xs text-muted-foreground">
                  {formatarData(post.scheduledFor || post.publishedAt)}
                </span>
              )}
            </div>

            <p className="line-clamp-3 text-sm">
              {post.content || post.title || "(sem texto)"}
            </p>

            <SelosDeRede post={post} />
          </div>
        </button>
      );
    })}
  </div>
);

// A lista: mesma informação em linhas, para comparar horários e achar o que
// falhou. É a visão de quem administra a agenda.
const Lista = ({ posts, onAbrir }) => (
  <div className="rounded-xl border">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Publicação</TableHead>
            <TableHead>Redes</TableHead>
            <TableHead>Situação</TableHead>
            <TableHead>Quando</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {posts.map((post) => (
            <TableRow
              key={idDoPost(post)}
              onClick={() => onAbrir(post)}
              className="cursor-pointer"
            >
              <TableCell className="max-w-md">
                <p className="truncate text-sm">
                  {post.content || post.title || "(sem texto)"}
                </p>
                {(post.platforms || []).some((alvo) => alvo.error) && (
                  <p className="mt-0.5 inline-flex items-center gap-1 text-xs text-destructive">
                    <TriangleAlert className="h-3 w-3" />
                    Falhou em pelo menos uma rede
                  </p>
                )}
              </TableCell>
              <TableCell>
                <SelosDeRede post={post} />
              </TableCell>
              <TableCell>
                <Situacao post={post} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                {formatarData(post.scheduledFor || post.publishedAt) || "—"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

// Detalhe de uma publicação. É onde o erro por rede fica legível — na lista
// ele não cabe, e sem ele uma publicação "parcial" parece publicada.
const DetalheModal = ({ post, onClose, onApagar }) => {
  if (!post) return null;

  const capa = (post.mediaItems || [])[0]?.url;

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Publicação
            <Situacao post={post} />
          </DialogTitle>
          <DialogDescription>
            {formatarData(post.scheduledFor || post.publishedAt) ||
              "Sem data definida"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {capa && (
            <img
              src={capa}
              alt=""
              className="max-h-64 w-full rounded-lg bg-muted object-contain"
            />
          )}

          <p className="whitespace-pre-wrap break-words text-sm">
            {post.content || post.title || "(sem texto)"}
          </p>

          <div className="space-y-2">
            {(post.platforms || []).map((alvo) => {
              const rede = identidadeDaRede(alvo.platform);
              const Glifo = rede.icone;

              return (
                <div
                  key={`${alvo.platform}-${alvo.accountId}`}
                  className="flex items-start gap-2 rounded-lg border p-2.5"
                >
                  <span
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-white"
                    style={{ backgroundColor: rede.corSelo }}
                  >
                    <Glifo className="h-3.5 w-3.5" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">{rede.rotulo}</p>
                    {alvo.error ? (
                      <p className="mt-0.5 text-xs text-destructive">
                        {alvo.error}
                      </p>
                    ) : (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {SITUACOES[alvo.status]?.rotulo || alvo.status || "—"}
                      </p>
                    )}
                  </div>

                  {alvo.platformPostUrl && (
                    <a
                      href={alvo.platformPostUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex shrink-0 items-center gap-1 text-xs text-primary hover:underline"
                    >
                      <ExternalLink className="h-3 w-3" />
                      Abrir
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={() => onApagar(post)}>
            <Trash2 className="h-4 w-4 text-destructive" />
            Apagar
          </Button>
          <Button onClick={onClose}>Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DIAS = [
  "segunda",
  "terça",
  "quarta",
  "quinta",
  "sexta",
  "sábado",
  "domingo"
];

// A Zernio devolve dia 0=segunda e hora em UTC. Converter aqui é o que faz a
// sugestão bater com o relógio de quem lê — sem isso, "quarta às 21h" seria
// mostrado para um horário que aqui é quarta às 18h.
const faixaParaLocal = (faixa) => {
  // Uma quarta-feira qualquer serve de âncora: o que importa é o deslocamento
  // do fuso, não a semana.
  const base = new Date(Date.UTC(2026, 0, 5 + faixa.day_of_week, faixa.hour));

  return {
    dia: DIAS[(base.getDay() + 6) % 7],
    hora: base.getHours(),
    engajamento: faixa.avg_engagement,
    posts: faixa.post_count
  };
};

// A próxima ocorrência de um dia/hora, no formato do input datetime-local.
const proximoHorario = (dia, hora) => {
  const alvo = DIAS.indexOf(dia);
  const agora = new Date();
  const hojeSegunda = (agora.getDay() + 6) % 7;

  let faltam = alvo - hojeSegunda;

  if (faltam < 0 || (faltam === 0 && agora.getHours() >= hora)) faltam += 7;

  const data = new Date(
    agora.getFullYear(),
    agora.getMonth(),
    agora.getDate() + faltam,
    hora,
    0
  );

  const pad = (n) => String(n).padStart(2, "0");

  return `${data.getFullYear()}-${pad(data.getMonth() + 1)}-${pad(
    data.getDate()
  )}T${pad(data.getHours())}:00`;
};

const NovaModal = ({ open, onClose, contas, onPublicar }) => {
  const [texto, setTexto] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [escolhidas, setEscolhidas] = useState([]);
  const [quando, setQuando] = useState("");
  const [enviando, setEnviando] = useState(false);

  const [urlDigitada, setUrlDigitada] = useState("");
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const arquivoRef = useRef(null);

  const [repostar, setRepostar] = useState(false);
  const [intervalo, setIntervalo] = useState("1");
  const [unidade, setUnidade] = useState("month");
  const [limite, setLimite] = useState("");

  const [horarios, setHorarios] = useState([]);

  useEffect(() => {
    if (!open) return;

    setTexto("");
    setMediaUrl("");
    setUrlDigitada("");
    setEscolhidas([]);
    setQuando("");
    setRepostar(false);
    setIntervalo("1");
    setUnidade("month");
    setLimite("");

    // Os melhores horários vêm do histórico das contas. Falta de dados não é
    // erro: a seção some e o campo de data segue valendo.
    (async () => {
      try {
        const { data } = await api.get("/zernio/posts/best-times");

        setHorarios(
          (data.slots || [])
            .slice(0, 3)
            .map(faixaParaLocal)
            .filter((faixa) => faixa.posts > 0)
        );
      } catch {
        setHorarios([]);
      }
    })();
  }, [open]);

  const subirArquivo = async (evento) => {
    const arquivo = evento.target.files?.[0];

    if (!arquivo) return;

    setEnviandoArquivo(true);

    try {
      const corpo = new FormData();
      corpo.append("file", arquivo);

      const { data } = await api.post("/zernio/upload", corpo, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      setMediaUrl(data.url);

      // Avisa agora, não na hora de publicar: em desenvolvimento o endereço é
      // localhost e a Zernio não alcança.
      if (!data.publica) {
        toast.warn(
          "A imagem subiu, mas este servidor não é alcançável da internet — a Zernio não vai conseguir baixá-la."
        );
      }
    } catch (err) {
      toastError(err);
    }

    setEnviandoArquivo(false);
    // Limpa para permitir reenviar o mesmo arquivo depois de remover.
    evento.target.value = "";
  };

  const alternar = (conta) =>
    setEscolhidas((atuais) =>
      atuais.includes(conta.accountId)
        ? atuais.filter((id) => id !== conta.accountId)
        : [...atuais, conta.accountId]
    );

  const enviar = async (agendar) => {
    if (!texto.trim() && !mediaUrl.trim()) {
      toast.error("Escreva um texto ou informe uma mídia.");
      return;
    }

    if (escolhidas.length === 0) {
      toast.error("Escolha ao menos uma conta.");
      return;
    }

    setEnviando(true);

    const ok = await onPublicar({
      content: texto,
      platforms: escolhidas.map((accountId) => {
        const conta = contas.find((item) => item.accountId === accountId);

        return { platform: conta.platform, accountId };
      }),
      ...(mediaUrl.trim()
        ? { mediaItems: [{ url: mediaUrl.trim(), type: "image" }] }
        : {}),
      // A Zernio quer ISO; o input datetime-local devolve hora local sem
      // fuso, e mandar cru faria o post sair três horas fora.
      ...(agendar ? { scheduledFor: new Date(quando).toISOString() } : {}),
      publishNow: !agendar,
      ...(repostar
        ? {
            recycling: {
              gap: Number(intervalo) || 1,
              gapFreq: unidade,
              expireCount: limite ? Number(limite) : null
            }
          }
        : {})
    });

    setEnviando(false);

    if (ok) onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nova publicação</DialogTitle>
          <DialogDescription>
            Sai pelas contas escolhidas. Quem publica e agenda é a Zernio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="zernio-texto">Texto</Label>
            <Textarea
              id="zernio-texto"
              rows={5}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="O que você quer publicar?"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Imagem</Label>

            {mediaUrl ? (
              <div className="flex items-start gap-3 rounded-lg border p-2">
                <img
                  src={mediaUrl}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-md bg-muted object-cover"
                />
                <p className="min-w-0 flex-1 break-all text-xs text-muted-foreground">
                  {mediaUrl}
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setMediaUrl("")}
                  aria-label="Remover imagem"
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {/* Enviar do computador é o caminho de sempre; a URL fica
                    para quem já tem a imagem hospedada em outro lugar. */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={enviandoArquivo}
                  onClick={() => arquivoRef.current?.click()}
                >
                  {enviandoArquivo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Enviar imagem
                </Button>

                <input
                  ref={arquivoRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={subirArquivo}
                />

                <span className="text-xs text-muted-foreground">ou</span>

                <Input
                  className="h-8 flex-1"
                  value={urlDigitada}
                  onChange={(e) => setUrlDigitada(e.target.value)}
                  onBlur={() =>
                    urlDigitada.trim() && setMediaUrl(urlDigitada.trim())
                  }
                  placeholder="colar uma URL pública"
                />
              </div>
            )}

            <p className="text-xs text-muted-foreground">
              A Zernio baixa o arquivo do nosso servidor para publicar, então o
              endereço precisa ser alcançável da internet. O Instagram exige
              imagem.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Contas</Label>
            <div className="max-h-44 space-y-1 overflow-y-auto rounded-lg border p-2">
              {contas.map((conta) => {
                const rede = identidadeDaRede(conta.platform);
                const Glifo = rede.icone;

                return (
                  <label
                    key={conta.accountId}
                    className="flex cursor-pointer items-center gap-2 rounded-md p-1.5 text-sm hover:bg-accent"
                  >
                    <Checkbox
                      checked={escolhidas.includes(conta.accountId)}
                      onCheckedChange={() => alternar(conta)}
                    />
                    <span
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-white"
                      style={{ backgroundColor: rede.corSelo }}
                    >
                      <Glifo className="h-3 w-3" />
                    </span>
                    <span className="min-w-0 flex-1 truncate">
                      {conta.name}
                    </span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {rede.rotulo}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="zernio-quando">Agendar para</Label>
            <Input
              id="zernio-quando"
              type="datetime-local"
              value={quando}
              onChange={(e) => setQuando(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Deixe vazio para publicar agora.
            </p>

            {/* Os horários que renderam mais engajamento no histórico. Um
                clique preenche a data — sugerir sem preencher obrigaria a
                pessoa a traduzir "quarta às 18h" para o calendário na mão. */}
            {horarios.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 pt-1">
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                  <Sparkles className="h-3 w-3" />
                  Melhores horários:
                </span>
                {horarios.map((faixa) => (
                  <Button
                    key={`${faixa.dia}-${faixa.hora}`}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7"
                    onClick={() =>
                      setQuando(proximoHorario(faixa.dia, faixa.hora))
                    }
                  >
                    {faixa.dia}, {String(faixa.hora).padStart(2, "0")}h
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* Repostagem: a Zernio recria cópias agendadas depois que o post
              sai. Só vale com data — num rascunho não há de onde contar o
              intervalo. */}
          <div className="space-y-2 rounded-lg border p-3">
            <label className="flex cursor-pointer items-start gap-2">
              <Checkbox
                className="mt-0.5"
                checked={repostar}
                onCheckedChange={(valor) => setRepostar(Boolean(valor))}
              />
              <span>
                <span className="flex items-center gap-1.5 text-sm font-medium">
                  <Repeat className="h-3.5 w-3.5" />
                  Repostar de tempos em tempos
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Depois de publicado, a Zernio agenda cópias no intervalo
                  escolhido. YouTube e TikTok não aceitam repostagem.
                </span>
              </span>
            </label>

            {repostar && (
              <div className="grid gap-2 pl-6 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="zernio-intervalo" className="text-xs">
                    A cada
                  </Label>
                  <Input
                    id="zernio-intervalo"
                    type="number"
                    min="1"
                    value={intervalo}
                    onChange={(e) => setIntervalo(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs">Período</Label>
                  <Select value={unidade} onValueChange={setUnidade}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">semanas</SelectItem>
                      <SelectItem value="month">meses</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="zernio-limite" className="text-xs">
                    Parar depois de
                  </Label>
                  <Input
                    id="zernio-limite"
                    type="number"
                    min="1"
                    placeholder="sem limite"
                    value={limite}
                    onChange={(e) => setLimite(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => enviar(true)}
            disabled={enviando || !quando}
          >
            {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
            <CalendarClock className="h-4 w-4" />
            Agendar
          </Button>
          <Button onClick={() => enviar(false)} disabled={enviando}>
            {enviando && <Loader2 className="h-4 w-4 animate-spin" />}
            <Send className="h-4 w-4" />
            Publicar agora
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Publicacoes = () => {
  const { contas } = useContasZernio();

  const [posts, setPosts] = useState([]);
  const [filtro, setFiltro] = useState("todos");
  const [visao, setVisao] = useState("quadro");
  const [carregando, setCarregando] = useState(true);
  const [novaAberta, setNovaAberta] = useState(false);
  const [detalhe, setDetalhe] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/zernio/posts", { params: { limit: 50 } });

      setPosts(data.posts || []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // O filtro roda aqui, não na API: a lista já veio inteira, e refazer a
  // chamada a cada clique gastaria requisição da Zernio para reordenar o que
  // está na memória.
  const visiveis = useMemo(() => {
    if (filtro === "todos") return posts;

    if (filtro === "scheduled") {
      return posts.filter((post) =>
        ["scheduled", "pending"].includes(post.status)
      );
    }

    return posts.filter((post) => post.status === filtro);
  }, [posts, filtro]);

  const contar = (valor) => {
    if (valor === "todos") return posts.length;
    if (valor === "scheduled") {
      return posts.filter((post) => ["scheduled", "pending"].includes(post.status))
        .length;
    }

    return posts.filter((post) => post.status === valor).length;
  };

  const publicar = async (corpo) => {
    try {
      await api.post("/zernio/posts", {
        ...corpo,
        timezone: "America/Sao_Paulo"
      });

      toast.success(
        corpo.publishNow ? "Publicação enviada." : "Publicação agendada."
      );

      await carregar();

      return true;
    } catch (err) {
      toastError(err);

      return false;
    }
  };

  const apagar = async (post) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Apagar esta publicação na Zernio?")) return;

    try {
      await api.delete(`/zernio/posts/${idDoPost(post)}`);
      toast.success("Publicação apagada.");
      setDetalhe(null);
      await carregar();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className="space-y-4 p-4">
      <Cabecalho
        titulo="Publicações"
        descricao="Publique agora ou agende nas contas conectadas. Quem publica e agenda é a Zernio."
        acao={
          <Button onClick={() => setNovaAberta(true)} disabled={!contas.length}>
            <Plus className="h-4 w-4" />
            Nova publicação
          </Button>
        }
      />

      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Filtros com a contagem: dá para ver que existem 3 com falha sem
            precisar abrir o filtro para descobrir. */}
        <div className="flex flex-wrap gap-1">
          {FILTROS.map((item) => (
            <Button
              key={item.valor}
              variant={filtro === item.valor ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setFiltro(item.valor)}
            >
              {item.rotulo}
              <Badge variant="outline" className="ml-1 tabular-nums">
                {contar(item.valor)}
              </Badge>
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-1 rounded-lg border p-0.5">
          <Button
            variant={visao === "quadro" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setVisao("quadro")}
            aria-label="Ver em quadros"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={visao === "lista" ? "secondary" : "ghost"}
            size="icon"
            className="h-7 w-7"
            onClick={() => setVisao("lista")}
            aria-label="Ver em lista"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {carregando ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : visiveis.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed px-6 py-12 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <ImageIcon className="h-5 w-5" />
          </span>
          <p className="mt-3 text-sm font-medium">
            {posts.length === 0
              ? "Nenhuma publicação por aqui"
              : "Nada com esse filtro"}
          </p>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            {contas.length === 0
              ? "Conecte uma conta em Redes sociais › Contas para publicar."
              : posts.length === 0
                ? "Crie a primeira em “Nova publicação”."
                : "Troque o filtro para ver as outras."}
          </p>
        </div>
      ) : visao === "quadro" ? (
        <Quadro posts={visiveis} onAbrir={setDetalhe} />
      ) : (
        <Lista posts={visiveis} onAbrir={setDetalhe} />
      )}

      <NovaModal
        open={novaAberta}
        contas={contas}
        onClose={() => setNovaAberta(false)}
        onPublicar={publicar}
      />

      <DetalheModal
        post={detalhe}
        onClose={() => setDetalhe(null)}
        onApagar={apagar}
      />
    </div>
  );
};

export default Publicacoes;
